// =====================================================================
// MPS-263 · KYC-101 — Country-specific identifier configuration
// Author: Akarsh Singh | 2026-08-29
//
// What a country asks a company to prove its identity with, and how to check
// the supplier typed it correctly.
//
// This is configuration, not data. A CIN's format is a legal fact that changes
// about once a year, so it lives in a file that goes through code review rather
// than in a database table anybody can edit.
//
// The patterns here catch a typo at the moment it is made, so a supplier is not
// waiting three days to be told they mistyped one character. They are NOT a
// security control: the real verification is an admin opening the government
// registry and confirming the company exists and is active. A malformed value
// that somehow reaches the server is caught there.
//
// ⚠  THE PATTERNS BELOW ARE UNVERIFIED. They must be confirmed by whoever owns
//    the compliance rules before this ships. A wrong pattern tells a supplier
//    their own valid registration number is invalid, and nobody can work out why.
// =====================================================================

/** One identifier a country asks for. */
export interface KycIdentifierField {
  /** Stable key stored against the captured value. Never shown to anyone. */
  key: string;
  /** What the supplier reads beside the input. */
  label: string;
  required: boolean;
  /** Typo catcher, applied on blur in the browser. Optional. */
  regex?: string;
  /** Shown when the pattern fails. Written for the supplier, not the developer. */
  error?: string;
  /** Shown to the ADMIN during review: where this identifier can be verified. */
  verifyAt?: string;
  /** Hint text under the field, for anything non-obvious. */
  hint?: string;
  type?: 'TEXT' | 'SELECT';
  /** The choices when type is SELECT. */
  options?: string[];
}

/**
 * A check across two fields, e.g. an Indian GSTIN carries the PAN inside it.
 * Expressed as data so a new country's rule is an edit here, not new code in
 * the component.
 */
export interface KycCrossFieldRule {
  code: string;
  /** Field a slice is taken from. */
  source: string;
  /** 1-based position the slice starts at. */
  start: number;
  /** How many characters the slice takes. */
  length: number;
  /** Field the slice must equal. */
  target: string;
  error: string;
}

export interface KycCountryRules {
  fields: KycIdentifierField[];
  rules: KycCrossFieldRule[];
}

/**
 * Bumped whenever any country's field set changes. Stored against every
 * captured value, so a record entered under older rules can still be read and
 * explained rather than silently reinterpreted under the new ones.
 */
export const KYC_COUNTRY_CONFIG_VERSION = 1;

/** Used for any country without rules of its own. */
export const KYC_DEFAULT_COUNTRY_KEY = 'DEFAULT';

/**
 * Keyed on countryMaster.code.
 *
 * Only the four countries the ticket names carry real rules. Every other
 * country falls to DEFAULT, which is the required behaviour rather than a gap:
 * an unlisted country gets a generic pair with no format checking, and the
 * admin verifies it manually exactly as they do today.
 *
 * A country earns an entry here when a real supplier from there needs one and
 * somebody has checked the format.
 */
export const KYC_COUNTRY_CONFIG: Record<string, KycCountryRules> = {

  // ---- India ----
  IN: {
    fields: [
      {
        key: 'CIN',
        label: 'Corporate Identity Number (CIN)',
        required: true,
        regex: '^[A-Z]\\d{5}[A-Z]{2}\\d{4}[A-Z]{3}\\d{6}$',
        error: 'CIN must be 21 characters, for example U74999MH2020PTC123456',
        verifyAt: 'mca.gov.in',
      },
      {
        key: 'PAN',
        label: 'PAN',
        required: true,
        regex: '^[A-Z]{5}\\d{4}[A-Z]$',
        error: 'PAN must be 10 characters, for example AAACV1234F',
        verifyAt: 'incometax.gov.in',
      },
      {
        key: 'GSTIN',
        label: 'GSTIN',
        required: true,
        regex: '^\\d{2}[A-Z]{5}\\d{4}[A-Z][A-Z0-9]{3}$',
        error: 'GSTIN must be 15 characters, for example 27AAACV1234F1Z5',
        verifyAt: 'gst.gov.in',
        hint: 'Characters 3 to 12 of your GSTIN are your PAN.',
      },
    ],
    rules: [
      {
        code: 'GSTIN_CONTAINS_PAN',
        source: 'GSTIN',
        start: 3,
        length: 10,
        target: 'PAN',
        error: 'GSTIN must contain your PAN in characters 3 to 12',
      },
    ],
  },

  // ---- Singapore ----
  SG: {
    fields: [
      {
        key: 'UEN',
        label: 'Unique Entity Number (UEN)',
        required: true,
        regex: '^[0-9A-Z]{9,10}$',
        error: 'UEN must be 9 or 10 characters, for example 201234567K',
        verifyAt: 'bizfile.gov.sg',
      },
      {
        key: 'GST_REG_NO',
        label: 'GST registration number',
        required: false,
        error: 'Enter your GST registration number, or leave it blank if you are not registered',
        verifyAt: 'mytax.iras.gov.sg',
      },
    ],
    rules: [],
  },

  // ---- United Arab Emirates ----
  // The licence is issued by one of several authorities, so which authority is
  // itself part of the identity and the admin needs it to know where to verify.
  AE: {
    fields: [
      {
        key: 'ISSUING_AUTHORITY',
        label: 'Issuing authority',
        required: true,
        type: 'SELECT',
        options: [
          'Department of Economic Development (DED)',
          'Abu Dhabi Global Market (ADGM)',
          'Dubai Multi Commodities Centre (DMCC)',
          'Jebel Ali Free Zone (JAFZA)',
          'Dubai International Financial Centre (DIFC)',
          'Sharjah Airport International Free Zone (SAIF)',
          'Other free zone authority',
        ],
        hint: 'The authority that issued your trade licence.',
      },
      {
        key: 'TRADE_LICENCE',
        label: 'Trade licence number',
        required: true,
        error: 'Enter the trade licence number exactly as printed on the licence',
        verifyAt: 'the portal of the issuing authority selected above',
      },
      {
        key: 'TRN',
        label: 'Tax Registration Number (TRN)',
        required: true,
        regex: '^\\d{15}$',
        error: 'TRN must be 15 digits',
        verifyAt: 'tax.gov.ae',
      },
    ],
    rules: [],
  },

  // ---- Netherlands ----
  NL: {
    fields: [
      {
        key: 'KVK',
        label: 'KVK number',
        required: true,
        regex: '^\\d{8}$',
        error: 'KVK number must be 8 digits',
        verifyAt: 'kvk.nl',
      },
      {
        key: 'VAT',
        label: 'VAT number (BTW-nummer)',
        required: true,
        regex: '^NL\\d{9}B\\d{2}$',
        error: 'VAT number must look like NL123456789B01',
        verifyAt: 'ec.europa.eu/taxation_customs/vies',
      },
    ],
    rules: [],
  },

  // ---- Everywhere else ----
  // Two free-text fields, no pattern. The admin verifies manually, which is
  // exactly what happens for every country today.
  DEFAULT: {
    fields: [
      {
        key: 'REGISTRATION_NUMBER',
        label: 'Business registration number',
        required: true,
        hint: 'As issued by the company registry in your country of incorporation.',
      },
      {
        key: 'TAX_ID',
        label: 'Tax identification number',
        required: true,
      },
    ],
    rules: [],
  },
};

/** The rules for a country code, falling back to the generic pair. */
export function getKycCountryRules(countryCode?: string | null): KycCountryRules {
  const key = (countryCode || '').trim().toUpperCase();
  return KYC_COUNTRY_CONFIG[key] || KYC_COUNTRY_CONFIG[KYC_DEFAULT_COUNTRY_KEY];
}
